// ── Shared 3D scene helpers for The Last Kingdom battle table ──
// Pure CSS 3D (no WebGL): keeps the board-game feel while giving real depth.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMotionValue, useSpring, useAnimationControls } from 'framer-motion';

export const SCENE = {
  perspective: 1150,
  perspectiveMobile: 780,
  tableTilt: 23,        // degrees the war table leans away from the camera
  tableTiltMobile: 12,
  cardDepth: 10,        // card stock thickness in px
  cardDepthMobile: 6,
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Global pointer / gyroscope parallax, normalised to -1..1.
 * Every background layer multiplies it by its own depth factor.
 */
export const useParallax = (enabled = true) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 45, damping: 18, mass: 0.7 });
  const sy = useSpring(y, { stiffness: 45, damping: 18, mass: 0.7 });

  useEffect(() => {
    if (!enabled) return undefined;
    const onMove = (e) => {
      x.set(clamp((e.clientX / window.innerWidth - 0.5) * 2, -1, 1));
      y.set(clamp((e.clientY / window.innerHeight - 0.5) * 2, -1, 1));
    };
    const onOrient = (e) => {
      if (e.gamma == null || e.beta == null) return;
      x.set(clamp(e.gamma / 30, -1, 1));
      y.set(clamp((e.beta - 45) / 30, -1, 1));
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('deviceorientation', onOrient);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('deviceorientation', onOrient);
    };
  }, [enabled, x, y]);

  return { px: sx, py: sy };
};

/**
 * Per-element pointer tilt — used by the cards and the heraldic plaques.
 * Returns motion values plus the handlers to spread on the element.
 */
export const useTilt = ({ max = 12, disabled = false } = {}) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const glare = useMotionValue(0.5);
  const srx = useSpring(rx, { stiffness: 260, damping: 22, mass: 0.5 });
  const sry = useSpring(ry, { stiffness: 260, damping: 22, mass: 0.5 });
  const sglare = useSpring(glare, { stiffness: 180, damping: 26 });

  const onPointerMove = useCallback((e) => {
    if (disabled) return;
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(clamp(nx * max * 2, -max, max));
    rx.set(clamp(-ny * max * 2, -max, max));
    glare.set(clamp(nx + 0.5, 0, 1));
  }, [disabled, max, rx, ry, glare]);

  const onPointerLeave = useCallback(() => {
    rx.set(0); ry.set(0); glare.set(0.5);
  }, [rx, ry, glare]);

  return { rx: srx, ry: sry, glare: sglare, handlers: { onPointerMove, onPointerLeave } };
};

/**
 * Camera shake for the whole scene — fired when a blow lands.
 * `shake(intensity)` with 0.4 (light) .. 1.6 (dragon fire).
 */
export const useScreenShake = () => {
  const controls = useAnimationControls();
  const shake = useCallback((intensity = 1) => {
    const a = 9 * intensity;
    controls.start({
      x: [0, -a, a * 0.85, -a * 0.55, a * 0.3, 0],
      y: [0, a * 0.5, -a * 0.35, a * 0.2, -a * 0.1, 0],
      rotate: [0, -0.5 * intensity, 0.45 * intensity, -0.2 * intensity, 0.1 * intensity, 0],
      transition: { duration: 0.5, ease: 'easeOut' },
    });
  }, [controls]);
  return { shakeControls: controls, shake };
};

/** Intensity of the camera shake for each card value. */
export const SHAKE_BY_CARD = { 1: 0.9, 2: 1.1, 3: 1.6, 4: 0, 5: 0, 6: 0, 7: 0.5, 8: 0.6, 9: 0.6, 10: 0.7 };

/**
 * Fires a callback on a loose interval — used for distant thunder,
 * catapult impacts and other ambient battlefield beats.
 */
export const useAmbientBeat = (cb, { min = 6000, max = 14000, enabled = true } = {}) => {
  const saved = useRef(cb);
  saved.current = cb;
  useEffect(() => {
    if (!enabled) return undefined;
    let timer;
    const tick = () => {
      timer = setTimeout(() => { saved.current?.(); tick(); }, min + Math.random() * (max - min));
    };
    tick();
    return () => clearTimeout(timer);
  }, [min, max, enabled]);
};

/** Respects the OS "reduce motion" setting — heavy layers switch themselves off. */
export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);
  return reduced;
};

/** Deterministic pseudo-random so particle fields don't reshuffle on every render. */
export const seeded = (seed) => {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
};
